import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import Select from 'react-select'
import {useFormik} from 'formik'
import * as yup from 'yup'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import useAppToast from 'src/hooks/useAppToast'
import {CUSTOMER} from 'src/app/_interfaces/customer.interface'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {getCustomersAsync} from 'src/redux/actions/customerAction'
import {setSelectedCustomerForReport} from 'src/redux/slices/customerSlice'
import {createCustomPlanAsync, updateCustomPlanAsync} from 'src/redux/actions/pricingActions'
import InfoTooltip from '../listing/components/InfoTooltip'
import {BillingPlans} from 'src/interfaces/Platform.interface'

type ManageFormValues = {
  user_id: string
  product_limit: string
  order_limit: string
  billing_frequency: string
  price: string
  billed_manually: string
}

type ManageCustomPlansLocationState = {
  mode?: 'edit'
  customPlan?: BillingPlans
}

type CancellableCustomerSearch = {
  abort: () => void
}

const normalizeBilledManuallyValue = (description?: string, billingFrequency?: string) => {
  const value = (description || '').trim().toUpperCase().replaceAll(' ', '_')

  if (['BILLED_MANUALLY', 'MANUAL', 'MANUALLY_BILLED', 'YES'].includes(value)) {
    return 'BILLED_MANUALLY'
  }
  if (['SHOPIFY', 'SHOPIFY_PLAN', 'CREATE_SHOPIFY_PLAN', 'NO_CREATE_SHOPIFY_PLAN'].includes(value)) {
    return 'SHOPIFY'
  }
  if (['PAYPAL', 'PAYPAL_PLAN', 'CREATE_PAYPAL_PLAN', 'NO_CREATE_PAYPAL_PLAN'].includes(value)) {
    return 'PAYPAL'
  }

  return billingFrequency ? 'PAYPAL' : ''
}

const validationSchema = yup.object().shape({
  product_limit: yup
    .number()
    .typeError('Accepts only numeric value')
    .required('Product limit is a required field')
    .positive('Product limit must be positive number'),
  order_limit: yup
    .number()
    .typeError('Accepts only numeric value')
    .required('Order limit is a required field')
    .positive('Order limit must be positive number'),
  billed_manually: yup.string().required('This field is required.'),
  billing_frequency: yup.string().when('billed_manually', {
    is: (val: string) => val !== 'BILLED_MANUALLY' && val !== '',
    then: (schema) => schema.required('Please select billing frequency'),
    otherwise: (schema) => schema.notRequired(),
  }),
  price: yup.number().when('billed_manually', {
    is: (val: string) => val !== 'BILLED_MANUALLY' && val !== '',
    then: (schema) =>
      schema
        .typeError('Accepts only numeric value')
        .required('Price limit is a required field')
        .positive('Price limit must be positive number'),
    otherwise: (schema) => schema.notRequired(),
  }),
})

const ManageCustomPlans: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const {successToast, warningToast} = useAppToast()
  const [customers, setCustomers] = useState<CUSTOMER[] | []>([])
  const [currentPage, setCurrentPage] = useState(1)
  const {totalCustomer, customerLoader, selectedCustomerForReport} = useAppSelector(
    (state) => state.customer
  )

  const routeState = (location.state || {}) as ManageCustomPlansLocationState
  const editPlan = routeState.customPlan
  const isEditMode = routeState.mode === 'edit' && !!editPlan

  const initialValues = useMemo<ManageFormValues>(
    () => ({
      user_id: editPlan?.user?.id ? String(editPlan.user.id) : '',
      product_limit: editPlan?.product_limit ? String(editPlan.product_limit) : '',
      order_limit: editPlan?.order_limit ? String(editPlan.order_limit) : '',
      billing_frequency: editPlan?.billing_frequency || '',
      price: editPlan?.price?.value !== undefined ? String(editPlan.price.value) : '',
      billed_manually: normalizeBilledManuallyValue(editPlan?.description, editPlan?.billing_frequency),
    }),
    [editPlan]
  )

  const [customerListPagination] = useState({
    page_limit: 10,
    page_num: 1,
    order_by: '-id',
    search: '',
    user_type: 'USER',
    from_date: '',
    to_date: '',
  })

  const SEARCH_MIN_CHARS = 2
  const SEARCH_DEBOUNCE_MS = 400
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const customerSearchRequestRef = useRef<CancellableCustomerSearch | null>(null)

  const runCustomerSearch = (search: string) => {
    customerSearchRequestRef.current?.abort()
    const request = dispatch(
      getCustomersAsync({...customerListPagination, page_num: 1, search})
    )
    customerSearchRequestRef.current = {abort: () => request.abort()}
    setCurrentPage(1)
    request.then((res: any) => {
      if (res.meta?.requestStatus === 'fulfilled') {
        setCustomers(res.payload?.data?.data || [])
      }
    })
  }

  const {values, isSubmitting, errors, touched, setFieldError, setValues, handleSubmit, handleChange, handleBlur} =
    useFormik<ManageFormValues>({
      initialValues,
      enableReinitialize: true,
      validationSchema,
      validateOnChange: true,
      onSubmit: (submittedValues, {setSubmitting, resetForm}) => {
        if (!selectedCustomerForReport && !isEditMode) {
          warningToast('Please select a user')
          setSubmitting(false)
          return
        }

        const selectedUserId = selectedCustomerForReport?.id || editPlan?.user?.id
        if (!selectedUserId) {
          warningToast('User not found')
          setSubmitting(false)
          return
        }

        const payload = {
          user_id: Number(selectedUserId),
          product_limit: submittedValues.product_limit,
          order_limit: submittedValues.order_limit,
          billed_manually: submittedValues.billed_manually,
          billing_frequency:
            submittedValues.billed_manually !== 'BILLED_MANUALLY'
              ? submittedValues.billing_frequency
              : undefined,
          price: submittedValues.billed_manually !== 'BILLED_MANUALLY' ? submittedValues.price : undefined,
        }

        const request = isEditMode && editPlan
          ? updateCustomPlanAsync({billing_plan_id: editPlan.id, ...payload})
          : createCustomPlanAsync({...submittedValues, user_id: selectedUserId})

        dispatch(request)
          .then((response: any) => {
            const {status_code, statusText, status} = response.payload || {}
            const {massage} = response.payload?.data || {}

            if ([200, 201].includes(status_code)) {
              successToast(massage || (isEditMode ? 'Plan updated successfully.' : 'Plan created successfully.'))
              if (isEditMode) {
                navigate('/pricing-plans')
                return
              }

              resetForm()
              setValues({
                user_id: '',
                product_limit: '',
                order_limit: '',
                billing_frequency: '',
                price: '',
                billed_manually: '',
              })
              dispatch(setSelectedCustomerForReport(null))
            } else if (status === 400) {
              warningToast(massage || (isEditMode ? 'Plan not updated.' : 'Plan already exists.'))
            } else if (status_code === 404) {
              warningToast(statusText || 'User not found')
            } else if (status_code === 500) {
              warningToast(massage || 'Internal Server Error')
            } else {
              warningToast(massage || 'Something went wrong.')
            }
          })
          .catch((error) => {
            console.log(isEditMode ? 'Error while updating custom plan:' : 'Error while creating custom plan:', error)
            warningToast(isEditMode ? 'Plan not updated. Something went wrong.' : 'Plan not created. Something went wrong.')
          })
          .finally(() => {
            setSubmitting(false)
          })
      },
    })

  useEffect(() => {
    dispatch(getCustomersAsync({...customerListPagination})).then((res: any) => {
      setCustomers(res.payload?.data?.data || [])
    })
  }, [customerListPagination, dispatch])

  useEffect(() => {
    if (isEditMode && editPlan?.user) {
      dispatch(setSelectedCustomerForReport(editPlan.user as unknown as CUSTOMER))
      return
    }
    dispatch(setSelectedCustomerForReport(null))
  }, [dispatch, isEditMode, editPlan])

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
      customerSearchRequestRef.current?.abort()
    }
  }, [])

  return (
    <div>
      <div className='mb-3'>
        <div className='d-flex align-items-center justify-content-between'>
          <span className='fw-bold text-lg ' style={{fontSize: '16px'}}>
            {isEditMode ? 'Edit Plan' : 'Add New'}
          </span>
          <Link to='/pricing-plans' className='fs-6 fw-semibold'>
            Return to List
          </Link>
        </div>
      </div>

      <div className='card border-0 shadow rounded '>
        <form onSubmit={handleSubmit}>
          <div className='card-body'>
            <div className='row g-4'>
              <div className='col-xl-3 col-lg-4 col-md-6 col-12'>
                <label className='form-label'>Select Customer</label>
                <Select
                  placeholder='Select User'
                  value={selectedCustomerForReport}
                  options={customers}
                  required
                  isDisabled={isEditMode}
                  isLoading={customerLoader}
                  getOptionLabel={(item) => (item.name ? `${item.name}- ${item.email}` : item.email)}
                  getOptionValue={(item) => item.id + ''}
                  isSearchable
                  styles={{
                    dropdownIndicator: (baseStyles) => ({...baseStyles, display: 'none'}),
                    indicatorSeparator: (baseStyles) => ({...baseStyles, display: 'none'}),
                  }}
                  onInputChange={(ev, actionMeta) => {
                    if (actionMeta.action !== 'input-change') {
                      return
                    }

                    if (searchDebounceRef.current) {
                      clearTimeout(searchDebounceRef.current)
                    }

                    const search = ev.trim()
                    if (search.length < SEARCH_MIN_CHARS) {
                      // Below the minimum: cancel any pending/in-flight search, don't hit the API.
                      customerSearchRequestRef.current?.abort()
                      return
                    }

                    searchDebounceRef.current = setTimeout(() => {
                      runCustomerSearch(search)
                    }, SEARCH_DEBOUNCE_MS)
                  }}
                  onBlur={handleBlur}
                  name='user_id'
                  onChange={(ev) => {
                    if (ev) {
                      dispatch(setSelectedCustomerForReport(ev))
                      setFieldError('react-select-2-input', '')
                      setFieldError('user_id', '')
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (currentPage < totalCustomer / 10) {
                      dispatch(getCustomersAsync({...customerListPagination, page_num: currentPage + 1})).then((res: any) => {
                        if (res.payload?.data?.data?.length) {
                          setCurrentPage(currentPage + 1)
                        }
                        setCustomers((prev) => [...prev, ...(res.payload?.data?.data || [])])
                      })
                    }
                  }}
                />
                {!selectedCustomerForReport && touched['react-select-2-input'] && errors.user_id && (
                  <span className='fs-8 text-danger'>{errors.user_id}</span>
                )}
              </div>

              <div className='col-xl-3 col-lg-4 col-md-6 col-12'>
                <label className='form-label'>
                  Billed Manually ?{' '}
                  <InfoTooltip
                    message={
                      <p className='mb-0'>
                        Yes, Show Subscribed Only : No PayPal plan will be created for existing users with manual billing. <br />
                        No, Create PayPal Plan : PayPal plan will be created for new users.
                      </p>
                    }
                  />
                </label>
                <select
                  required
                  value={values.billed_manually}
                  name='billed_manually'
                  disabled={isEditMode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className='form-select form-select-lg'
                >
                  <option value=''>Select One</option>
                  <option value='BILLED_MANUALLY'>Yes, Show Subscribed Only</option>
                  <option value='PAYPAL'>No, Create PayPal Plan</option>
                  <option value='SHOPIFY'>No, Create Shopify Plan</option>
                </select>
                {touched.billed_manually && errors.billed_manually && (
                  <span className='fs-8 text-danger'>{errors.billed_manually}</span>
                )}
              </div>

              <div className='col-xl-3 col-lg-4 col-md-6 col-12'>
                <label className='form-label'>Product/SKU Limit</label>
                <input
                  required
                  type='text'
                  value={values.product_limit}
                  placeholder='ex. - 1000'
                  name='product_limit'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className='form-control form-control-lg'
                />
                {touched.product_limit && errors.product_limit && (
                  <span className='fs-8 text-danger'>{errors.product_limit}</span>
                )}
              </div>

              <div className='col-xl-3 col-lg-4 col-md-6 col-12'>
                <label className='form-label'>Order Limit</label>
                <input
                  required
                  type='text'
                  value={values.order_limit}
                  placeholder='ex. - 1000'
                  name='order_limit'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className='form-control form-control-lg'
                />
                {touched.order_limit && errors.order_limit && (
                  <span className='fs-8 text-danger'>{errors.order_limit}</span>
                )}
              </div>

              <div className='col-xl-3 col-lg-4 col-md-6 col-12'>
                <label className='form-label'>Plan Price (USD)</label>
                <input
                  required={values.billed_manually !== 'BILLED_MANUALLY' && values.billed_manually !== ''}
                  type='text'
                  value={values.price}
                  placeholder='ex. - 1299'
                  name='price'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className='form-control form-control-lg'
                />
                {touched.price && errors.price && <span className='fs-8 text-danger'>{errors.price}</span>}
              </div>

              <div className='col-xl-3 col-lg-4 col-md-6 col-12'>
                <label className='form-label'>Billing Frequency</label>
                <select
                  required={values.billed_manually !== 'BILLED_MANUALLY' && values.billed_manually !== ''}
                  value={values.billing_frequency}
                  name='billing_frequency'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className='form-select form-select-lg'
                >
                  <option value=''>Select One</option>
                  <option value='EVERY_30_DAYS'>Monthly</option>
                  <option value='ANNUAL'>Annually</option>
                </select>
                {touched.billing_frequency && errors.billing_frequency && (
                  <span className='fs-8 text-danger'>{errors.billing_frequency}</span>
                )}
              </div>
            </div>

            <div className='d-flex mt-4 justify-content-end'>
              <button disabled={isSubmitting} type='submit' className='btn btn-sm btn-success fw-bold'>
                {isSubmitting ? (isEditMode ? 'Updating Plan...' : 'Saving Plan...') : isEditMode ? 'Update Plan' : 'Add Plan'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ManageCustomPlans
